import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Eye, Sparkles, MessageCircle } from 'lucide-react';
import templateAdminService from '../services/templateAdminService';
import './TemplatePreviewPanel.css';

/**
 * Supported locales for the preview language selector.
 * Matches the 20 locales supported by the ZoomClient.
 */
const PREVIEW_LOCALES = [
    { code: 'en',    label: 'English'                },
    { code: 'ar',    label: 'Arabic'                 },
    { code: 'de',    label: 'German'                 },
    { code: 'es',    label: 'Spanish'                },
    { code: 'fr',    label: 'French'                 },
    { code: 'hi',    label: 'Hindi'                  },
    { code: 'id',    label: 'Indonesian'             },
    { code: 'it',    label: 'Italian'                },
    { code: 'ja',    label: 'Japanese'               },
    { code: 'ko',    label: 'Korean'                 },
    { code: 'nl',    label: 'Dutch'                  },
    { code: 'pl',    label: 'Polish'                 },
    { code: 'pt-BR', label: 'Portuguese (Brazil)'    },
    { code: 'ru',    label: 'Russian'                },
    { code: 'sv',    label: 'Swedish'                },
    { code: 'th',    label: 'Thai'                   },
    { code: 'tr',    label: 'Turkish'                },
    { code: 'vi',    label: 'Vietnamese'             },
    { code: 'zh-CN', label: 'Chinese (Simplified)'   },
    { code: 'zh-TW', label: 'Chinese (Traditional)'  },
];

const DEFAULT_LANG = 'en';
const DEFAULT_TOP_N = 5;

/**
 * TemplatePreviewPanel
 *
 * Read-only preview panel that shows how active question templates
 * appear to end users as suggestion chips in the Meeting AI panel.
 * Mimics the ZoomClient MeetingAiPanel chip layout.
 *
 * @param {number} refreshKey - Increments to trigger a re-fetch after CRUD operations.
 */
const TemplatePreviewPanel = ({ refreshKey }) => {
    const [lang, setLang]           = useState(DEFAULT_LANG);
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            try {
                const data = await templateAdminService.fetchActiveTemplates(lang, DEFAULT_TOP_N);
                if (!cancelled) setTemplates(data);
            } catch (_err) {
                if (!cancelled) setTemplates([]);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();

        return () => { cancelled = true; };
    }, [lang, refreshKey]);

    return (
        <div className="preview-container" data-testid="preview-container">
            {/* ── Header ── */}
            <div className="preview-header">
                <div className="preview-header-left">
                    <Eye size={15} className="preview-eye-icon" />
                    <span className="preview-title">Preview</span>
                    <span className="preview-subtitle">How templates appear to users</span>
                </div>

                <select
                    className="preview-language-selector"
                    value={lang}
                    onChange={e => setLang(e.target.value)}
                    aria-label="Preview language"
                >
                    {PREVIEW_LOCALES.map(({ code, label }) => (
                        <option key={code} value={code}>{label}</option>
                    ))}
                </select>
            </div>

            {/* ── Device frame ── */}
            <div className="preview-device">
                {/* Simulated ZoomClient meeting AI panel header */}
                <div className="preview-device-header">
                    <MessageCircle size={13} />
                    <span>Ask AI</span>
                </div>

                {/* Chips area */}
                <div className="preview-chips-area">
                    {isLoading ? (
                        <div className="preview-loading">
                            <span className="preview-loading-dot" />
                            <span className="preview-loading-dot" />
                            <span className="preview-loading-dot" />
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="preview-empty">
                            <p>No active templates</p>
                            <p className="preview-empty-hint">Activate templates above to see them here</p>
                        </div>
                    ) : (
                        <div className="preview-chips" data-testid="preview-chips">
                            {templates.map(template => (
                                template.isSummary ? (
                                    <button
                                        key={template.templateKey}
                                        className="preview-chip preview-chip-summary"
                                        type="button"
                                        disabled
                                        data-testid={`preview-chip-summary-${template.templateKey}`}
                                    >
                                        <Sparkles size={13} />
                                        {template.text}
                                    </button>
                                ) : (
                                    <button
                                        key={template.templateKey}
                                        className="preview-chip"
                                        type="button"
                                        disabled
                                        data-testid={`preview-chip-${template.templateKey}`}
                                    >
                                        {template.text}
                                    </button>
                                )
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

TemplatePreviewPanel.propTypes = {
    refreshKey: PropTypes.number.isRequired,
};

export default TemplatePreviewPanel;
